import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchMe, getToken, login as apiLogin, setToken } from "../api/client";

interface User {
  id: number;
  email: string;
  display_name: string | null;
  role: string;
  is_owner?: boolean;
  permissions: string[];
  granular?: Record<string, { view: boolean; edit: boolean; amounts: boolean }>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** True wanneer user permissie heeft (of admin wildcard * of eigenaar) */
  hasPerm: (perm: string) => boolean;
  /** True wanneer user granulair recht heeft op nieuw permission_items.key */
  canView: (itemKey: string) => boolean;
  canEdit: (itemKey: string) => boolean;
  canSeeAmounts: (itemKey: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    fetchMe()
      .then((u) => setUser({ ...u, permissions: u.permissions ?? [] }))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setToken(res.access_token);
    setUser({ ...res.user, permissions: res.user.permissions ?? [] });
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const hasPerm = useCallback(
    (perm: string) => {
      if (!user) return false;
      if (user.is_owner) return true;
      return user.permissions.includes("*") || user.permissions.includes(perm);
    },
    [user],
  );

  const canView = useCallback(
    (itemKey: string) => {
      if (!user) return false;
      if (user.is_owner) return true;
      return user.granular?.[itemKey]?.view ?? false;
    },
    [user],
  );

  const canEdit = useCallback(
    (itemKey: string) => {
      if (!user) return false;
      if (user.is_owner) return true;
      return user.granular?.[itemKey]?.edit ?? false;
    },
    [user],
  );

  const canSeeAmounts = useCallback(
    (itemKey: string) => {
      if (!user) return false;
      if (user.is_owner) return true;
      return user.granular?.[itemKey]?.amounts ?? false;
    },
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, hasPerm, canView, canEdit, canSeeAmounts }),
    [user, loading, login, logout, hasPerm, canView, canEdit, canSeeAmounts],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
